import React, { useState } from 'react';
import * as XLSX from "xlsx";

import {
  SubHeader,
  OrdersTable,
  StyledButton,
  ExportModal,
} from '../../components';
import { useUsers } from '../../context/users';

// Normalize a single address field for comparison (trim, lowercase, collapse spaces).
const norm = (s?: string | null) => (s ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

// A comparable key for an order's address, ignoring notes/parking/building.
const addressKey = (a?: { addressLine1: string; addressLine2: string; city: string; state: string; zip: string }) =>
  a ? [a.addressLine1, a.addressLine2, a.city, a.state, a.zip].map(norm).join('|') : '';

const Orders = (): JSX.Element => {
  const [showExportModal, setShowExportModal] = useState(false);
  const { orders, users, units } = useUsers();

  const downloadExcel = (data) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, "DataSheet.xlsx");
  };

  // Build the export rows, prepending a few derived columns:
  //  - Tenant Name: orders only store tenantID, so look up the readable name.
  //  - Unit: the tenant's unit ID, matching what the Inventory table shows.
  //  - Address Check: error-check flag — a DELIVERY whose address matches one of
  //    the tenant's PICKUP orders likely means the address was never updated.
  const exportOrders = () =>
    downloadExcel(
      orders.map(o => {
        const tenantName = users.find(u => u.id === o.tenantID)?.name ?? '';
        const unit = units.find(u => u.tenantID === o.tenantID)?.unitID ?? '';

        let addressCheck = '';
        if (o.jobType === 'DELIVERY') {
          const deliveryKey = addressKey(o.address);
          const matchesPickup =
            deliveryKey !== '' &&
            orders.some(
              p =>
                p.tenantID === o.tenantID &&
                p.jobType === 'PICKUP' &&
                addressKey(p.address) === deliveryKey,
            );
          if (matchesPickup) addressCheck = '⚠ Same as pickup address';
        }

        return { 'Tenant Name': tenantName, Unit: unit, 'Address Check': addressCheck, ...o };
      }),
    );

  return (
    <>
      <SubHeader title="Orders" dropdown="custom" />
      <div style={{ paddingTop: 16 }}>
        <OrdersTable type="All Orders" height="65vh" />
      </div>

      <div style={{ paddingTop: 16, paddingBottom: 32 }}>
        <StyledButton
          label="Export"
          onClick={exportOrders}
        />
      </div>

      {showExportModal && (
        <ExportModal setShow={setShowExportModal} type="orders" />
      )}
    </>
  );
};

export default Orders;
